import React from 'react';
import clsx from 'clsx';
import { PlaylistCard } from '@components/Playlists/basics/PlaylistCard';
import { usePlaylistCardInfo } from '@components/Playlists/basics/usePlaylistCardInfo';
import { addSearchHistory } from '@components/SearchBar/SearchHistory/useSearchHistoryStore';
import { SearchSongCard } from '@components/SongCards/SearchSongCard';
import { ArtistUserCard } from '@components/basics/UserCard/ArtistUserCard';
import { CollectorUserCard } from '@components/basics/UserCard/CollectorUserCard';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  PlaylistCardDetailsFragmentDoc,
  SearchResultArtistFragmentDoc,
  SearchResultCollectorFragmentDoc,
  SearchResultReleaseFragmentDoc,
} from '@graphql/reactQuery';
import { SearchArtistRow } from './Rows/SearchArtistRow';
import { SearchCollectorRow } from './Rows/SearchCollectorRow';
import { SearchPlaylistRow } from './Rows/SearchPlaylistRow';
import { SearchSongRow } from './Rows/SearchSongRow';

/*
 * These components are wrappers around PlaylistCard, CollectorUserCard, ArtistUserCard and SearchSongCard
 * allowing to add user click to Recent Searches without modifying the actual card components
 */

export function PlaylistSearchCard({
  data,
  rowInMobile,
}: {
  data: FragmentType<PlaylistCardDetailsFragmentDoc>;
  rowInMobile: boolean;
}) {
  const playlist = getFragment(PlaylistCardDetailsFragmentDoc, data);

  const { id, playlistName, username } = usePlaylistCardInfo({ data: playlist });

  return (
    <div
      onClick={() => {
        addSearchHistory({
          id,
          name: playlistName,
          subText: username,
          type: 'Playlist',
        });
      }}
    >
      <div className={clsx(rowInMobile && 'hidden md:block')}>
        <PlaylistCard data={data} showMetadata={false} />
      </div>
      {rowInMobile && <SearchPlaylistRow data={data} />}
    </div>
  );
}

export function CollectorSearchCard({
  collector,
  rowInMobile,
}: {
  collector: FragmentType<SearchResultCollectorFragmentDoc>;
  rowInMobile: boolean;
}) {
  const { id, username } = getFragment(SearchResultCollectorFragmentDoc, collector);
  return (
    <div
      onClick={() => {
        addSearchHistory({
          id,
          name: username,
          type: 'Collector',
          subText: null,
        });
      }}
    >
      <div className={clsx(rowInMobile && 'hidden md:block')}>
        {collector && <CollectorUserCard collector={collector} />}
      </div>
      {rowInMobile && <SearchCollectorRow data={collector} />}
    </div>
  );
}

export function ArtistSearchCard({
  artist,
  rowInMobile,
}: {
  artist: FragmentType<SearchResultArtistFragmentDoc>;
  rowInMobile: boolean;
}) {
  const { id, name } = getFragment(SearchResultArtistFragmentDoc, artist);
  return (
    <div
      onClick={() => {
        addSearchHistory({
          id,
          name,
          type: 'Artist',
          subText: null,
        });
      }}
    >
      <div className={clsx(rowInMobile && 'hidden md:block')}>
        <ArtistUserCard artist={artist} />
      </div>
      {rowInMobile && <SearchArtistRow data={artist} />}
    </div>
  );
}

export function ReleaseSearchCard({
  data,
  getNextTrackIds,
  rowInMobile,
}: {
  data: FragmentType<SearchResultReleaseFragmentDoc>;
  getNextTrackIds: (() => string[]) | undefined;
  rowInMobile: boolean;
}) {
  const release = getFragment(SearchResultReleaseFragmentDoc, data);

  const artistName = release.artist.name;
  const releaseTitle = release.title;

  return (
    <div
      onClick={() => {
        addSearchHistory({
          id: release.id,
          type: 'Release',
          name: releaseTitle,
          subText: artistName,
        });
      }}
    >
      <div className={clsx(rowInMobile && 'hidden md:block')}>
        <SearchSongCard data={data} getNextTrackIds={getNextTrackIds} />
      </div>
      {rowInMobile && <SearchSongRow data={data} />}
    </div>
  );
}
